import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";

// Template157: Montmartre (Sacré-Cœur, calles, Place du Tertre). Todo desde backend.
// page.texts[0]=header, [1]=título portada, [2]=intro | page.sections[0..3] (imageId, title, body, sabiasQue[0]; section1 solo body box, section3 box con imagen float) | page.images (portada, sacre-core, calle-montmatre, montmatre-pintor), page.canvasImages (logo, montmatre-sticker)
const Template157 = ({
	backgroundColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found) return found.src ?? found.url;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const headerText = page?.texts?.[0]?.content;
	const portadaTitle = page?.texts?.[1]?.content;
	const introText = page?.texts?.[2]?.content;
	const logoImage = getCanvasImageUrl("logo");

	const portadaImage = getImageSrc("portada");
	const montmatreSticker = getCanvasImageUrl("montmatre-sticker");

	const sections = Array.isArray(page?.sections) ? page.sections : [];
	const section0 = sections[0];
	const section1 = sections[1];
	const section2 = sections[2];
	const section3 = sections[3];

	const sacreCoreImage = getImageSrc(section0?.imageId);
	const calleMontmatreImage = getImageSrc(section2?.imageId);
	const montmatrePintorImage = getImageSrc(section3?.imageId);

	const actualHeaderColor = headerBackgroundColor || "#2C3E50";

	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "13px",
		color: "#444",
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
			}}
		>
			{headerText && (
				<Box
					data-block-id="header"
					data-template-header
					sx={{
						backgroundColor: actualHeaderColor,
						color: headerTextColor || "#fff",
						textAlign: "center",
						padding: "15px 0",
						fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
						fontSize: "14px",
						fontWeight: 600,
						flexShrink: 0,
						position: "sticky",
						top: 0,
						zIndex: 99999,
					}}
				>
					{headerText}
					{!isSmallScreen && logoImage && (
						<img
							src={logoImage}
							alt=""
							style={{
								width: 80,
								position: "absolute",
								right: 2,
								top: 18,
								zIndex: 999999,
							}}
						/>
					)}
				</Box>
			)}

			{/* PORTADA */}
			{portadaImage && (
				<Box data-block-id="image-0" sx={{ position: "relative", marginBottom: "12px" }}>
					<img
						src={portadaImage}
						alt=""
						style={{ width: "100%", height: 200, objectFit: "cover" }}
					/>
					{portadaTitle && (
						<Box
							component="h1"
							sx={{
								fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
								fontSize: isSmallScreen ? "28px" : "32px",
								textAlign: "center",
								position: "absolute",
								bottom: "0",
								margin: "10px",
								left: "20px",
								color: "#fff",
								textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
							}}
						>
							{portadaTitle}
						</Box>
					)}
					{montmatreSticker && (
						<img
							src={montmatreSticker}
							alt=""
							style={{
								width: 90,
								position: "absolute",
								bottom: 60,
								left: 18,
							}}
						/>
					)}
				</Box>
			)}

			<Box data-block-id="text-1" sx={{ padding: "10px 12px", paddingTop: 0, position: "relative" }}>
				{introText && (
					<Box
						component="p"
						sx={{ ...estiloP, marginBottom: "12px", marginTop: "-10px" }}
					>
						{introText}
					</Box>
				)}

				<Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
					{/* Sacré-Cœur */}
					{section0 && (
						<Box data-block-id="text-2" sx={{ display: "flex", gap: "16px", position: "relative" }}>
							{sacreCoreImage && (
								<img
									src={sacreCoreImage}
									alt=""
									style={{
										width: "40%",
										height: 110,
										objectFit: "cover",
										borderRadius: "12px",
									}}
								/>
							)}
							<Box sx={{ width: sacreCoreImage ? "60%" : "100%" }}>
								{section0.title && (
									<Box component="h3" sx={estiloH3}>
										{section0.title}
									</Box>
								)}
								{section0.body && (
									<Box component="p" sx={estiloP}>
										{section0.body}
									</Box>
								)}
							</Box>
							{section0.sabiasQue?.[0] && (
								<Box sx={{ position: "absolute", top: -10, left: "33%" }}>
									<SabiasQue
										icono={section0.sabiasQue[0].icono}
										texto={section0.sabiasQue[0].texto}
									/>
								</Box>
							)}
						</Box>
					)}

					{/* Funicular - solo body en caja azul */}
					{section1?.body && (
						<Box data-block-id="text-3" sx={{ display: "flex", gap: "16px", position: "relative" }}>
							<Box sx={{ width: "100%" }}>
								<Box
									component="p"
									sx={{
										...estiloP,
										padding: "12px",
										backgroundColor: "#E8F4F8",
										borderRadius: "12px",
									}}
								>
									{section1.body}
								</Box>
							</Box>
						</Box>
					)}

					{/* Calles de Montmartre */}
					{section2 && (
						<Box data-block-id="text-4" sx={{ display: "flex", gap: "16px", position: "relative" }}>
							{calleMontmatreImage && (
								<img
									src={calleMontmatreImage}
									alt=""
									style={{
										width: "40%",
										height: 110,
										objectFit: "cover",
										borderRadius: "12px",
									}}
								/>
							)}
							<Box sx={{ width: calleMontmatreImage ? "60%" : "100%" }}>
								{section2.title && (
									<Box component="h3" sx={estiloH3}>
										{section2.title}
									</Box>
								)}
								{section2.body && (
									<Box component="p" sx={estiloP}>
										{section2.body}
									</Box>
								)}
							</Box>
							{section2.sabiasQue?.[0] && (
								<Box
									sx={{
										position: "absolute",
										top: isSmallScreen ? 0 : -10,
										left: "33%",
									}}
								>
									<SabiasQue
										icono={section2.sabiasQue[0].icono}
										texto={section2.sabiasQue[0].texto}
									/>
								</Box>
							)}
						</Box>
					)}

					{/* Place du Tertre - box con imagen float */}
					{section3 && (
						<Box data-block-id="text-5" sx={{ display: "flex", gap: "16px", position: "relative" }}>
							<Box sx={{ width: "100%" }}>
								<Box
									component="p"
									sx={{
										...estiloP,
										padding: "12px",
										backgroundColor: "#E8F4F8",
										borderRadius: "12px",
									}}
								>
									{montmatrePintorImage && (
										<img
											src={montmatrePintorImage}
											alt=""
											style={{
												width: isSmallScreen ? 105 : 115,
												float: "right",
												marginRight: -34,
												marginTop: -40,
												borderRadius: 12,
											}}
										/>
									)}
									{section3.title && (
										<Box component="h3" sx={estiloH3}>
											{section3.title}
										</Box>
									)}
									{section3.body}
								</Box>
							</Box>
						</Box>
					)}
				</Box>
			</Box>
		</Box>
	);
};

Template157.thumbnail = "/images/template-1.png";

export default Template157;
